import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatDatepickerInputEvent,
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MiqMoment, normalizePolishNumber, formatNumberValue, isCELocale } from '@miq/utils';
import { NUMPAD_PERIOD, PERIOD, DASH, NUMPAD_MINUS } from '@angular/cdk/keycodes';
import { IOption, InputProps, DateRangeFilterOptions, UTC_EDP_OUTPUT_FORMAT } from '@miq/shared-models';
import { DataType, InputType, LeaseColumns } from '@miq/catalog';
import { CbreAlertService } from '@cbre/alerts';
import moment from 'moment';
import { displayRangeButtonsList } from '../range-button/range-button.constants';
import { disableRangeItem } from './range-item.helper';
import { NgClass } from '@angular/common';
import { FilterOptionComponent } from '../filter-option/filter-option.component';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { OnlyNumbersDirective } from '../../directives/only-numbers.directive';
import { L10nTranslatePipe } from 'angular-l10n';

export enum NumberInputType {
  Text = 'text',
  Number = 'number',
}
/**
 * Range item component for CE (Central European) locales that use comma as decimal separator.
 * - Space+comma (pl-PL, fi-FI, hu-HU, nb-NO, sv-SE, pt-PT): "1 234,56"
 * - Dot+comma   (de-DE, de-AT, it-IT, nl-NL, da-DK, es-ES, ro-RO): "1.234,56"
 * Renders numeric inputs as type="text" to avoid browser rejection of
 * formatted values like "1 234" or "1.234" which are invalid for type="number".
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-ce-range-item',
  templateUrl: './range-item.component.html',
  styleUrl: './range-item.component.scss',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    FilterOptionComponent,
    MatFormField,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    OnlyNumbersDirective,
    L10nTranslatePipe,
  ],
})
export class CECountryRangeItemComponent implements OnInit, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  filter = input<IOption>();
  aggregation = input<any>();
  form = input<FormGroup>();
  isLandingPage = input<any>();
  isLeaseExpirePage = input<any>();
  baseForm = input<any>();
  emitKeyField = input(false);
  removeFilter = output<void>();
  rangeChange = output<any>();
  tabInfo = input<any>();
  minDate: string | moment.Moment;
  maxDate: string | moment.Moment;
  displayFromClearButton: boolean;
  displayToClearButton: boolean;
  rangeFormGroup: FormGroup;
  restrictedKeyCodes = [];
  allowedKeyCodes = [];
  numberInputType: NumberInputType = isCELocale()
    ? NumberInputType.Text
    : NumberInputType.Number;
  fromControlValue;
  toControlValue;
  disableFilter = false;
  constructor(
    private fb: FormBuilder,
    private cbreAlertService: CbreAlertService
  ) {}
  ngOnInit(): void {
    this.filter().value = !!this.filter().label ? this.filter().label : this.filter().value;
    this.filter().key = !!this.filter().key ? this.filter().key : this.filter().field;
    let fromValue = this.getValueByDataType(this.fromControl.value);
    this.minDate = fromValue;
    this.fromControlValue = this.getValueByDataType(this.fromControl.value);
    let toValue = this.getValueByDataType(this.toControl.value);

    if (this.isCELocaleNumericType()) {
      fromValue = formatNumberValue(fromValue);
      toValue = formatNumberValue(toValue);
    }
    this.toControlValue = this.getValueByDataType(this.toControl.value);
    this.maxDate = toValue;
    const inputPropsFilter = this.filter() as InputProps;
    /**
     * The default behavior for inputs should be to update on blur event
     *  to avoid making a request on each character entered by the user
     */
    this.rangeFormGroup = this.fb.group({
      from: [fromValue, inputPropsFilter.controlOptions || { updateOn: 'blur' }],
      to: [toValue, inputPropsFilter.controlOptions || { updateOn: 'blur' }],
    });
    this.rangeFormGroup.valueChanges.subscribe(({ from, to }) => {
      // For Polish locale: strip display formatting ("12 345" → 12345) before storing/emitting
      // so the globalRangeGroup and parent always receive the plain numeric value
      let normalizedFrom = from;
      let normalizedTo = to;
      if (this.isCELocaleNumericType()) {
        normalizedFrom = normalizePolishNumber(from);
        normalizedTo = normalizePolishNumber(to);
      }
      this.globalRangeGroup.patchValue({
        from: this.formatDateValue(normalizedFrom, 'from'),
        to: this.formatDateValue(normalizedTo, 'to'),
      });
      const emitKey = this.emitKeyField() ? this.filter().key : this.filter().field;
      this.rangeChange.emit({ [emitKey]: this.globalRangeGroup.value });
    });
    this.globalRangeGroup.valueChanges.subscribe(({ from, to }) => {
      const isRangBtnRelatedField = displayRangeButtonsList[this.tabInfo()?.tabName]?.includes(
        this.filter().key.toString()
      );
      if (isRangBtnRelatedField) {
        /* disable the related-range-filter when range-button has selected with value */
        this.disableFilter = disableRangeItem(this);
      }
      if (from == null && to == null) {
        this.clearDateFilter('from', false);
        this.clearDateFilter('to', false);
      } else if (!this.filter().allowNegativeNumbers) {
        let fromVal = this.getValueByDataType(from);
        let toVal = this.getValueByDataType(to);
        if (this.isCELocaleNumericType()) {
          fromVal = formatNumberValue(fromVal);
          toVal = formatNumberValue(toVal);
        }
        this.rangeFormGroup.controls['from'].setValue(fromVal, { emitEvent: false });
        this.rangeFormGroup.controls['to'].setValue(toVal, { emitEvent: false });
        this.displayFromClearButton = this.isLEEndDateCtrl() ? false : !!fromVal;
        this.displayToClearButton = this.isLEEndDateCtrl() ? false : !!toVal;
      }
      /**
       * Validate the min and max values in case of number range filter
       * If min value is greater than or equal to max value, show error message.
       */
      if (
        this.filter().dataType === DataType.Numeric ||
        this.filter().dataType === DataType.Integer ||
        this.filter().dataType === DataType.Int
      ) {
        this.validateMinMaxNumberRange();
      }
      this.cdr.markForCheck();
    });
    this.displayFromClearButton = Boolean(this.fromControl.value);
    this.displayToClearButton = Boolean(this.toControl.value);
    // Add restricted key codes for filters that should only allow numbers
    this.restrictedKeyCodes =
      this.filter().dataType === DataType.Integer && this.filter().inputType === InputType.Number.toLowerCase()
        ? [NUMPAD_PERIOD, PERIOD]
        : [];
    this.allowedKeyCodes = this.filter().allowNegativeNumbers ? [DASH, NUMPAD_MINUS] : [];
    this.addEnumCtrlForDates();
  }
  private getValueByDataType(value: any) {
    let result = value;
    if (this.isDataTypeDate()) {
      const dateValue = moment(value, MiqMoment.utcInputFormat).utc();
      result = dateValue.isValid() ? dateValue : null;
    }
    return result;
  }
  dateEvent(type: 'from' | 'to', matDatepickerInputEvent: MatDatepickerInputEvent<moment.Moment>) {
    if (type === 'from') {
      this.minDate = matDatepickerInputEvent.value;
      this.displayFromClearButton = true;
      if (this.isLeaseExpirePage()) {
        this.doLEFromToAction(matDatepickerInputEvent, type);
        return;
      }
    } else {
      this.maxDate = matDatepickerInputEvent.value;
      this.displayToClearButton = true;
      if (this.isLeaseExpirePage()) {
        this.doLEFromToAction(matDatepickerInputEvent, type);
        return;
      }
    }
    // If a date is changed manually by the user it is necessary to clean the selected period
    this.updateEnumSelectionControl(null);
  }
  clearDateFilter(filterType: 'from' | 'to', emitEvent = true) {
    this.rangeFormGroup.controls[filterType].reset(null, { emitEvent });
    if (filterType === 'from') {
      this.displayFromClearButton = false;
      this.minDate = null;
      return;
    }
    if (filterType === 'to') {
      this.maxDate = null;
    }
    this.displayToClearButton = false;
  }
  private isDataTypeDate(): boolean {
    return this.filter().dataType === 'timestamp with time zone' || this.filter().dataType === 'date';
  }
  private formatDateValue(controlValue: string | moment.Moment, filterType: 'from' | 'to') {
    if (controlValue && this.isDataTypeDate()) {
      const momentTransformingMethod = filterType === 'from' ? 'startOf' : 'endOf';
      const date = moment(controlValue, MiqMoment.utcInputFormat).utc();
      controlValue = date[momentTransformingMethod]('day').format();
    }
    return controlValue;
  }
  get globalRangeGroup(): FormGroup | null {
    return (this.form()?.controls?.[this.filter().key] || this.form()?.controls?.[this.filter().field]) as FormGroup;
  }
  get fromControl(): FormControl | null {
    return (this.globalRangeGroup?.controls?.['from'] as FormControl) || null;
  }
  get toControl(): FormControl | null {
    return (this.globalRangeGroup?.controls?.['to'] as FormControl) || null;
  }
  get enumSelectionControl(): FormControl {
    return (this.parentGroupControl as FormGroup).controls['enumSelection'] as FormControl;
  }
  private updateEnumSelectionControl(value: null | DateRangeFilterOptions) {
    if (this.enumSelectionControl) {
      this.enumSelectionControl.setValue(value);
    }
  }
  ngOnDestroy() {
    /** label translation purpose added */
  }
  /**
   * Validate the min and max values in case of number range filter
   * If min value is greater than or equal to max value, show error message.
   */
  public validateMinMaxNumberRange() {
    const minValue = Number(this.fromControl.value);
    const maxValue = Number(this.toControl.value);
    const hasMinValue = ![null, undefined, ''].includes(this.fromControl.value);
    const hasMaxValue = ![null, undefined, ''].includes(this.toControl.value);
    if (hasMinValue && hasMaxValue && !isNaN(minValue) && !isNaN(maxValue) && minValue >= maxValue) {
      this.handleRangeError(true);
    }
  }
  public updateForm(event, ctrlName: 'from' | 'to'): void {
    const { value } = event.target;
    if (ctrlName === 'from') {
      const maxValue = Number(this.toControl.value);
      const numValue = Number(value);
      if (maxValue && numValue != 0) {
        if (numValue >= maxValue) {
          return this.handleRangeError(false);
        }
        this.fromControl.patchValue(value);
      }
    } else if (ctrlName === 'to') {
      const minValue = Number(this.fromControl.value);
      const numValue = Number(value);
      if (minValue && numValue != 0) {
        if (minValue >= numValue) {
          return this.handleRangeError(true);
        }
        this.toControl.patchValue(value);
      }
    } else {
      throw new Error('No valid name control range');
    }
  }
  public handleRangeError(shouldBeGreater: boolean): void {
    const errorMessage = shouldBeGreater
      ? 'detailsErrors.error.sections.maxValueLessThanMin'
      : 'detailsErrors.error.sections.minValueGreaterThanMax';
    this.cbreAlertService.showErrorSnackBar_l10n(errorMessage);
  }
  private addEnumCtrlForDates() {
    if (this.filter().dataType === DataType.Date || this.filter().dataType === DataType.TimestampWithTimeZone) {
      (this.parentGroupControl as FormGroup).addControl('enumSelection', new FormControl());
    }
  }
  get parentGroupControl(): FormGroup | FormControl {
    return (this.form().controls[this.filter().key] || this.form().controls[this.filter().field]) as
      | FormGroup
      | FormControl;
  }
  doLEFromToAction(matDatepickerInputEvent, type) {
    let maxDate = moment().add(36, 'months');
    let minDate = moment().subtract(36, 'months');
    let minDate_v = minDate.subtract(1, 'day').format(UTC_EDP_OUTPUT_FORMAT);
    let maxDate_v = maxDate.add(1, 'day').format(UTC_EDP_OUTPUT_FORMAT);
    if (type === 'from') {
      if (matDatepickerInputEvent.value.isBefore(minDate_v)) {
        const fromVal = this.fromControlValue;
        this.rangeFormGroup.controls['from'].setValue(fromVal, { emitEvent: false });
        this.cbreAlertService.showErrorSnackBar_l10n('recordsSearch.min36MonthsForLease', {
          duration: 5000,
        });
      } else {
        this.fromControlValue = this.minDate;
      }
    } else {
      if (matDatepickerInputEvent.value.isAfter(maxDate_v)) {
        const toVal = this.toControlValue;
        this.rangeFormGroup.controls['to'].setValue(toVal, { emitEvent: false });
        this.cbreAlertService.showErrorSnackBar_l10n('recordsSearch.max36MonthsForLease', {
          duration: 5000,
        });
      } else {
        this.toControlValue = this.maxDate;
      }
    }
  }
  isLEEndDateCtrl() {
    return this.isLeaseExpirePage() && this.filter().key === LeaseColumns.Terms_EndDate;
  }

  private isCELocaleNumericType(): boolean {
    return (
      isCELocale() &&
      (this.filter().dataType === DataType.Numeric || this.filter().dataType === DataType.Integer)
    );
  }
}
